/**
 * ENG-9567 — `agt agent restore-transcripts <code-name>`.
 *
 * Places a migrated agent's archived transcripts on THIS host and re-pins the
 * conversation it was having, so the next spawn resumes rather than starting
 * cold. Operator-invoked and deliberately not automatic; ENG-9568 is what makes
 * a migration do this for itself.
 *
 * The command is a thin shell: the control plane resolves the source address
 * and mints a prefix-scoped credential, `restoreAgentTranscripts` owns every
 * decision, and this file only wires them together and renders the verdict.
 */
import { api, ApiError } from '../lib/api-client.js';
import {
  describeUnreadableManifests,
  findDrainManifest,
  listSourceObjects,
  restoreTranscriptDeps,
  type RestoreSource,
} from '../lib/restore-transcripts-aws.js';
import {
  restoreAgentTranscripts,
  type RestoreResult,
  type RestoreStatus,
} from '../lib/restore-transcripts.js';

interface RestoreCredentialsResponse {
  ok: boolean;
  bucket: string;
  key_prefix: string;
  region: string | null;
  address_source: string;
  credentials: {
    access_key_id: string;
    secret_access_key: string;
    session_token: string;
    expiration: string;
  };
}

export interface RestoreTranscriptsOptions {
  from?: string;
  bucket?: string;
  keyPrefix?: string;
  region?: string;
  timezone?: string;
  json?: boolean;
}

/**
 * Exit code per outcome. Only a genuinely resumable restore is 0.
 *
 * `resume-disabled` is the one worth stating outright: it places nothing and
 * exits NON-zero, because on a host with AGT_DISABLE_SESSION_RESUME set the
 * spawner mints a fresh session id no matter what is on disk. A zero there
 * would tell an operator the agent's continuity was restored when the next
 * spawn is guaranteed to ignore every byte of it.
 */
export function exitCodeFor(status: RestoreStatus): number {
  return status === 'restored' ? 0 : status === 'partial' ? 0 : 1;
}

/**
 * Render an API failure for a human.
 *
 * `ApiError.message` is `body.error` — the machine CODE (`ambiguous_source_host`,
 * `no_recorded_address`). Printing that alone hands the operator a token with no
 * explanation and, worse, drops the `candidates` and `message` the route sends
 * specifically so they know what to do next. Both of this route's 409s are
 * designed as actionable refusals; without this they arrive as bare codes.
 */
export function describeCredentialError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return (err as Error)?.message ?? 'unknown error';
  }
  const body = err.body ?? {};
  const code = (body['error'] as string) ?? `HTTP ${err.status}`;
  const detail = body['message'] as string | undefined;
  const candidates = Array.isArray(body['candidates'])
    ? (body['candidates'] as unknown[]).map(String)
    : [];
  const parts = [code];
  if (detail) parts.push(`\n\n${detail}`);
  if (candidates.length > 0) parts.push(`\n\nCandidate hosts: ${candidates.join(', ')}`);
  return parts.join('');
}

/**
 * The `--json` payload.
 *
 * `ok` is derived from the SAME predicate as the exit code. It was computed
 * independently as `status === 'restored'`, so a `partial` run — which exits 0,
 * because the pinned conversation does resume — reported success through the
 * exit code and failure through `ok`, and two automation paths reading one run
 * disagreed about whether it worked.
 *
 * Extracted so that agreement is testable: asserting on `exitCodeFor` alone
 * leaves the payload's own predicate unexercised, which is how a sabotage
 * reintroducing the second definition survived.
 */
export function restoreJsonPayload(
  result: RestoreResult,
  messages: string[],
): Record<string, unknown> {
  return { ok: exitCodeFor(result.status) === 0, ...result, messages };
}

/**
 * Did the credential expire while the restore was running?
 *
 * The mint is deliberately short-lived (15 min). A large archive can outrun it,
 * and when it does every remaining `aws s3 cp` fails with an expired-token
 * error — which the restore core counts as ordinary dropped objects and reports
 * as `partial` or `failed`. The operator is then looking at a wall of
 * per-object download errors for a cause that has nothing to do with the
 * objects. Naming it is the difference between "re-run this" and "investigate
 * the archive".
 */
export function credentialExpiredDuringRun(
  expiration: string | null | undefined,
  nowMs: number,
): boolean {
  if (!expiration) return false;
  const expMs = Date.parse(expiration);
  // An unparseable expiry is not evidence of expiry — claiming it would send
  // the operator after the wrong cause just as surely.
  return Number.isFinite(expMs) && nowMs >= expMs;
}

/**
 * Which required fields the credential response failed to supply.
 *
 * Returns names rather than a boolean so the operator is told WHAT was missing:
 * "no usable credential" and "the API sent a body without a session token" send
 * a person to different places.
 */
export function missingCredentialFields(body: Partial<RestoreCredentialsResponse>): string[] {
  const missing: string[] = [];
  if (typeof body?.bucket !== 'string' || !body.bucket) missing.push('bucket');
  if (typeof body?.key_prefix !== 'string' || !body.key_prefix) missing.push('key_prefix');
  const c = body?.credentials;
  if (!c || typeof c !== 'object') {
    missing.push('credentials');
    return missing;
  }
  for (const f of ['access_key_id', 'secret_access_key', 'session_token'] as const) {
    if (typeof c[f] !== 'string' || !c[f]) missing.push(`credentials.${f}`);
  }
  return missing;
}

export async function agentRestoreTranscriptsCommand(
  codeName: string,
  opts: RestoreTranscriptsOptions = {},
): Promise<void> {
  // Collected as well as printed. The restore core's messages carry the REASON
  // behind a non-restored status, and a JSON consumer that got only the status
  // would have the verdict with none of the diagnosis.
  const messages: string[] = [];
  const log = (msg: string): void => {
    messages.push(msg);
    if (!opts.json) console.log(msg);
  };

  let minted: RestoreCredentialsResponse;
  try {
    minted = await api.post<RestoreCredentialsResponse>(
      `/agents/${encodeURIComponent(codeName)}/restore-credentials`,
      {
        source_host: opts.from,
        bucket: opts.bucket,
        key_prefix: opts.keyPrefix,
        region: opts.region,
      },
    );
  } catch (err) {
    const detail = describeCredentialError(err);
    // The 409s are the actionable ones and their bodies already say what to do
    // (record an archive read role / pass --bucket + --key-prefix), so the
    // message is surfaced rather than flattened into "request failed".
    if (opts.json) {
      console.log(
        JSON.stringify({ ok: false, status: 'credentials_failed', detail, messages }, null, 2),
      );
    } else {
      console.error(`Could not obtain restore credentials for '${codeName}': ${detail}`);
    }
    process.exitCode = 1;
    return;
  }

  // VALIDATED, not trusted. `RestoreCredentialsResponse` is a hand-declared
  // shape over an unvalidated JSON body — `api.post` checks nothing. A 200 that
  // omitted `credentials` would throw a TypeError right here, escaping the
  // function, and the operator would get a raw rejection from Commander's
  // top-level catch instead of the `credentials_failed` verdict and the JSON
  // diagnostics. (The expiry check further down already used `?.`, so the code
  // disagreed with itself about whether the field was guaranteed.)
  const missing = missingCredentialFields(minted);
  if (missing.length > 0) {
    const detail = `the API returned no usable credential (missing: ${missing.join(', ')})`;
    if (opts.json) {
      console.log(JSON.stringify({ ok: false, status: 'credentials_failed', detail, messages }, null, 2));
    } else {
      console.error(`Could not obtain restore credentials for '${codeName}': ${detail}`);
    }
    process.exitCode = 1;
    return;
  }

  const src: RestoreSource = {
    bucket: minted.bucket,
    keyPrefix: minted.key_prefix,
    region: minted.region,
    credentials: {
      accessKeyId: minted.credentials.access_key_id,
      secretAccessKey: minted.credentials.secret_access_key,
      sessionToken: minted.credentials.session_token,
    },
  };

  log(`Reading s3://${src.bucket}/${src.keyPrefix}/ (address from ${minted.address_source})`);

  const listing = await listSourceObjects(src);
  if (!listing.ok) {
    // With ListBucket granted (ENG-8814) a missing prefix lists EMPTY rather
    // than 403, so a failure here is a real access or network fault — not the
    // "nothing archived" case, which falls through to 'no-manifest' below.
    if (opts.json) {
      console.log(
        JSON.stringify(
          { ok: false, status: 'listing_failed', detail: listing.detail, messages },
          null,
          2,
        ),
      );
    } else {
      console.error(`Could not list the source prefix: ${listing.detail}`);
    }
    process.exitCode = 1;
    return;
  }

  const lookup = await findDrainManifest(src, codeName, listing.objects);
  if (lookup.kind === 'unavailable') {
    // Handled HERE rather than in the restore core, at the same layer as the
    // listing failure above: both are the inputs failing to arrive, not a
    // decision about what to place. Reporting it as 'no-manifest' would tell an
    // operator whose entire history is sitting in Glacier that nothing was ever
    // archived for this agent.
    const detail = describeUnreadableManifests(lookup.unreadable);
    if (opts.json) {
      console.log(
        JSON.stringify({ ok: false, status: 'manifest_unavailable', detail, messages }, null, 2),
      );
    } else {
      console.error(
        `Could not read the drain manifest for '${codeName}', so whether it was archived here is ` +
          `unknown — this is NOT the same as nothing having been archived: ${detail}`,
      );
    }
    process.exitCode = 1;
    return;
  }

  const result = await restoreAgentTranscripts(
    codeName,
    restoreTranscriptDeps({
      src,
      objects: listing.objects,
      manifest: lookup.kind === 'found' ? lookup.manifest : null,
      timezone: opts.timezone,
      log,
    }),
  );

  // Checked only when something actually failed: an expiry that elapsed after a
  // clean run explains nothing and would be noise on every slow success.
  if (
    (result.dropped > 0 || result.status === 'failed') &&
    credentialExpiredDuringRun(minted.credentials?.expiration, Date.now())
  ) {
    log(
      `[restore] the restore credential expired at ${minted.credentials.expiration} while this run ` +
        `was in progress, so the ${result.dropped} failed object(s) are most likely expired-token ` +
        `errors rather than a problem with the archive. Re-run to mint a fresh credential (ENG-9567).`,
    );
  }

  if (opts.json) {
    console.log(
      JSON.stringify(restoreJsonPayload(result, messages), null, 2),
    );
  } else {
    log('');
    log(`  status:  ${result.status}`);
    log(`  placed:  ${result.placed}`);
    log(`  skipped: ${result.skipped} (already current)`);
    log(`  dropped: ${result.dropped}`);
    log(`  of:      ${result.total} archived file(s)`);
    log(`  pinned:  ${result.pinRestored ? (result.pinnedSessionId ?? 'yes') : 'no'}`);
    if (result.destDir) log(`  into:    ${result.destDir}`);
    log('');
    log(
      result.status === 'restored'
        ? `'${codeName}' will resume its previous conversation on the next spawn.`
        : `'${codeName}' will NOT fully resume — see the reason above.`,
    );
  }

  process.exitCode = exitCodeFor(result.status);
}
